import { useState, useEffect, useMemo } from 'react'

import { isValidPhoneNumber } from 'react-phone-number-input'

import AuthService from '../../../utilities/services/auth.service'

import { useModal } from '../../../providers/ModalProvider/ModalProvider'

import { emailPatternMatch, namePatternMatch } from '../../../utilities/helpers'

import { editOrderAssociatedTo } from '../../../utilities/api'

import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Stack from 'react-bootstrap/Stack'

import { PhoneNumberInput } from '../../PhoneNumberInput'
import { Success, Error } from '../../ModalContent'
import { Spinner } from '../../LoadingContainer/Spinner'

function getAssociatedToDefaults(order) {
  const user = order?.users_permissions_user
  return {
    purchasedByOrganizationName: order?.purchasedByOrganizationName || '',
    firstName: order?.firstName || user?.firstName || '',
    lastName: order?.lastName || user?.lastName || '',
    phoneNumber: order?.phoneNumber || user?.phoneNumber || '',
    email: order?.email || user?.email || '',
  }
}

export default function ViewAssociatedTo({ order, onSuccess }) {
  const { closeModal } = useModal()
  const [updating, setUpdating] = useState(false)
  const [modalState, setModalState] = useState('base')
  const organization = AuthService.getOrg()
  const associatedToDefaults = useMemo(
    () => getAssociatedToDefaults(order),
    [order]
  )

  // Form state
  const [formData, setFormData] = useState(associatedToDefaults)

  // Validation state
  const [validationErrors, setValidationErrors] = useState({
    email: false,
    phoneNumber: false,
  })

  // Track if form has been modified
  const [isModified, setIsModified] = useState(false)

  // Check if form data has changed from original
  useEffect(() => {
    const hasChanged =
      formData.purchasedByOrganizationName !==
        associatedToDefaults.purchasedByOrganizationName ||
      formData.firstName !== associatedToDefaults.firstName ||
      formData.lastName !== associatedToDefaults.lastName ||
      formData.phoneNumber !== associatedToDefaults.phoneNumber ||
      formData.email !== associatedToDefaults.email

    setIsModified(hasChanged)
  }, [formData, associatedToDefaults])

  // remove email error when email changes
  useEffect(() => {
    setValidationErrors((prev) => ({
      ...prev,
      email: false,
    }))
  }, [formData?.email])

  // remove phoneNumber error when phoneNumber changes
  useEffect(() => {
    setValidationErrors((prev) => ({
      ...prev,
      phoneNumber: false,
    }))
  }, [formData?.phoneNumber])

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  // Handle email validation
  const handleValidEmail = (e) => {
    const { value } = e.target
    const isValid = value && emailPatternMatch(value)
    setValidationErrors((prev) => ({
      ...prev,
      email: value && !isValid,
    }))
  }

  // Handle phone number validation
  const handleValidNumber = () => {
    const isValid =
      formData.phoneNumber && isValidPhoneNumber(formData.phoneNumber)
    setValidationErrors((prev) => ({
      ...prev,
      phoneNumber: formData.phoneNumber && !isValid,
    }))
  }

  // Check if form is valid for saving
  const isFormValid = () => {
    const emailValid = !formData.email || emailPatternMatch(formData.email)
    const phoneValid =
      !formData.phoneNumber || isValidPhoneNumber(formData.phoneNumber)
    return emailValid && phoneValid
  }

  // Handle save
  const handleSave = async (e) => {
    e.preventDefault()
    setUpdating(true)
    const data = { ...formData }
    data.order = order
    data.organizationUUID = organization?.[0]?.uuid

    await editOrderAssociatedTo(data)
      .then((res) => {
        setUpdating(false)
        onSuccess()
        setModalState('success')
      })
      .catch((err) => {
        console.log('err: ', err)
        setUpdating(false)
        setModalState('error')
      })
  }

  const canSave = isModified && isFormValid()

  if (modalState === 'error') {
    return (
      <Error
        title="Something went wrong..."
        description=""
        onClose={closeModal}
      />
    )
  }

  if (modalState === 'success') {
    return (
      <Success
        title="Changes saved successfully!"
        description=""
        onClose={closeModal}
      />
    )
  }

  return (
    <>
      <Modal.Header closeButton className="border-0 pb-0 mb-4">
        <Stack direction="vertical" gap={1}>
          <Modal.Title className="h4 text-dark">Associated to</Modal.Title>
          <small className="text-muted">Order ID: #{order?.orderId}</small>
        </Stack>
      </Modal.Header>

      <Modal.Body className="pb-4 px-4 pt-0">
        <Form onSubmit={handleSave}>
          <Form.Group className="form-group mb-3" controlId="organizationName">
            <Form.Label>Organization Name</Form.Label>
            <Form.Control
              type="text"
              value={formData.purchasedByOrganizationName}
              onChange={(e) =>
                handleInputChange('purchasedByOrganizationName', e.target.value)
              }
              placeholder="Enter organization name"
            />
          </Form.Group>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="form-group mb-3" controlId="firstName">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  pattern={namePatternMatch}
                  value={formData.firstName}
                  required
                  onChange={(e) =>
                    handleInputChange(
                      'firstName',
                      e.target.validity.valid || e.target.value === ''
                        ? e.target.value
                        : formData.firstName
                    )
                  }
                  placeholder="Enter first name"
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="form-group mb-3" controlId="lastName">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  pattern={namePatternMatch}
                  required
                  value={formData.lastName}
                  onChange={(e) =>
                    handleInputChange(
                      'lastName',
                      e.target.validity.valid || e.target.value === ''
                        ? e.target.value
                        : formData.lastName
                    )
                  }
                  placeholder="Enter last name"
                />
              </Form.Group>
            </div>
          </div>

          <Form.Group className="form-group mb-3" controlId="email">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="Enter email address"
              className={validationErrors.email ? 'border-danger' : ''}
              onBlur={handleValidEmail}
            />
            {validationErrors.email && <Error type="invalidEmail" />}
          </Form.Group>

          <Form.Group className="form-group mt-0 mb-3" controlId="phoneNumber">
            <Form.Label>Phone Number</Form.Label>
            <PhoneNumberInput
              phoneNumber={formData.phoneNumber}
              setPhoneNumber={(value) =>
                handleInputChange('phoneNumber', value)
              }
              onBlur={handleValidNumber}
              hasError={validationErrors.phoneNumber}
              isRequired={false}
            />
          </Form.Group>
          <Modal.Footer className="border-0 p-0">
            <Button
              variant="outline-light"
              onClick={closeModal}
              className="px-4 me-2"
            >
              Close
            </Button>

            <Button
              variant="primary"
              type="submit"
              className="px-4"
              disabled={!canSave || updating}
              style={{ minWidth: 146 }}
            >
              {updating ? <Spinner variant="white" /> : 'Save changes'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal.Body>
    </>
  )
}
